import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { ConfigProvider, useMarketplaceEnabled } from "./context/ConfigContext";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import CaseEditor from "./pages/CaseEditor";
import LawyerPortal from "./pages/LawyerPortal";
import AdminPanel from "./pages/AdminPanel";
import Resources from "./pages/Resources";
import BusinessSearch from "./pages/BusinessSearch";
import FeeWaivers from "./pages/FeeWaivers";
import LegalLibrary from "./pages/LegalLibrary";
import FreeLegalHelp from "./pages/FreeLegalHelp";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import MfaSetup from "./pages/MfaSetup";
import MfaVerify from "./pages/MfaVerify";
import MfaSettings from "./pages/MfaSettings";
import Account from "./pages/Account";
import CaseStudy from "./pages/CaseStudy";
import Contact from "./pages/Contact";
import PolicyAcceptanceGate from "./components/PolicyAcceptanceGate";
import RequireMfa from "./components/RequireMfa";

function MarketplaceOnly({ children }) {
  const enabled = useMarketplaceEnabled();
  if (!enabled) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/rd-case-study" element={<CaseStudy />} />
      <Route path="/contact" element={<Contact />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/case/:id"
        element={
          <ProtectedRoute>
            <CaseEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lawyer"
        element={
          <ProtectedRoute>
            <MarketplaceOnly>
              <RequireMfa>
                <LawyerPortal />
              </RequireMfa>
            </MarketplaceOnly>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <RequireMfa>
              <AdminPanel />
            </RequireMfa>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            <Resources />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources/business-search"
        element={
          <ProtectedRoute>
            <BusinessSearch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources/fee-waivers"
        element={
          <ProtectedRoute>
            <FeeWaivers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources/legal-library"
        element={
          <ProtectedRoute>
            <LegalLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources/free-legal-help"
        element={
          <ProtectedRoute>
            <FreeLegalHelp />
          </ProtectedRoute>
        }
      />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mfa/setup"
        element={
          <ProtectedRoute>
            <MfaSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mfa/verify"
        element={
          <ProtectedRoute>
            <MfaVerify />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/security"
        element={
          <ProtectedRoute>
            <MfaSettings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ConfigProvider>
          <AuthProvider>
            <AppRouter />
            <PolicyAcceptanceGate />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </ConfigProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
